import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, FileText, Calendar, Mic, Users, Send, Award } from "lucide-react";
import { useMemberAuth } from "./MemberAuthContext";
import MemberQuotaCard from "./MemberQuotaCard";
import MemberDeclarations from "./MemberDeclarations";
import { EducatorSessionsPanel } from "./EducatorSessionsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberHome() {
  const { member } = useMemberAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/members/invite-stats`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch((e) => { if (e?.response?.status !== 401) console.warn("[Home] invite-stats:", e?.message); });
  }, []);

  const year = new Date().getFullYear();
  const acceptedThisYear = stats?.accepted_by_year?.[String(year)] || 0;

  const sections = [
    { to: "/membros/convites", icon: Send, title: "Convidar para um evento", body: "Traga consigo empresários e executivos que possam beneficiar da comunidade. Convites pessoais com 1 clique." },
    { to: "/membros/percursos", icon: BookOpen, title: "Percursos de auto-educação", body: "Caminhos pessoais e comunitários inspirados pela pedagogia kentenichiana." },
    { to: "/membros/retiros", icon: Calendar, title: "Retiros e encontros", body: "Calendário dos próximos retiros, dias de deserto e encontros locais." },
    { to: "/membros/documentos", icon: FileText, title: "Documentos internos", body: "Atas, relatórios, comunicações da direção e documentos de referência." },
    { to: "/membros/formacao", icon: Mic, title: "Áudios e vídeos", body: "Catequeses, testemunhos e palestras dos encontros internacionais." },
    { to: "/membros/diretorio", icon: Users, title: "Diretório de sócios", body: "Conhecer e contactar outros empresários e executivos da comunidade." },
  ];

  return (
    <section className="container-page py-12" data-testid="member-home">
      <div className="overline">Bem-vindo(a)</div>
      <h1 className="display-heading text-4xl sm:text-5xl mt-3">Olá, {firstName(member?.name)}.</h1>
      <span className="accent-rule mt-6 block" />
      <p className="mt-6 max-w-2xl text-[hsl(var(--muted-foreground))] leading-relaxed">
        Esta é a sua casa digital como sócio do CIEES Portugal. Aqui encontra os conteúdos, ferramentas e a comunidade que sustentam a sua caminhada de líder cristão no mundo do trabalho.
      </p>

      {/* Sessões onde sou o educador (só aparece quando aplicável) */}
      <EducatorSessionsPanel />

      {/* Ambassador badge */}
      {stats && (stats.accepted > 0 || stats.total > 0) && (
        <div
          className="mt-8 inline-flex items-start gap-4 bg-[hsl(var(--primary))] text-white px-5 py-4 max-w-xl"
          data-testid="member-ambassador-badge"
        >
          <Award className="h-6 w-6 text-[hsl(var(--accent))] shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/70">Embaixador CIEES {year}</div>
            <div className="display-heading text-xl mt-1">
              {acceptedThisYear === 0 && stats.pending === 0
                ? "Convide alguém para o próximo evento."
                : acceptedThisYear === 0
                  ? `Tem ${stats.pending} ${stats.pending === 1 ? "convite à espera de resposta" : "convites à espera de resposta"}.`
                  : `${acceptedThisYear} ${acceptedThisYear === 1 ? "pessoa trazida" : "pessoas trazidas"} por si este ano.`}
            </div>
            <Link
              to="/membros/convites"
              className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent))] hover:text-white"
            >
              Convidar mais <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {sections.map((s) => (
          <Link
            key={s.to}
            to={s.to}
            className="group block bg-white border border-[hsl(var(--border))] p-7 hover:border-[hsl(var(--primary))] transition-all"
            data-testid={`member-card-${s.to.replace(/\//g, "-")}`}
          >
            <s.icon className="h-6 w-6 text-[hsl(var(--accent))]" />
            <h3 className="mt-5 display-heading text-2xl group-hover:text-[hsl(var(--primary))] transition-colors">
              {s.title}
            </h3>
            <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{s.body}</p>
            <div className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--primary))]">
              Entrar <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        ))}

        <div className="bg-white border border-[hsl(var(--border))] p-7" data-testid="member-card-donate">
          <ArrowRight className="h-6 w-6 text-[hsl(var(--accent))] rotate-[-45deg]" />
          <h3 className="mt-5 display-heading text-2xl">Apoiar a comunidade</h3>
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
            Além da quota anual, pode contribuir com um donativo livre na página pública de apoio.
          </p>
          <Link to="/apoiar" className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--primary))]">
            Apoiar agora <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <MemberQuotaCard />
      </div>

      <div className="mt-6">
        <MemberDeclarations />
      </div>
    </section>
  );
}

function firstName(name) {
  if (!name) return "amigo(a)";
  return name.split(" ")[0];
}
